<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractStatusTransition',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getCurrentOptions = (...args) => {
    switch (args[0]) {
      case 'approved':
        return {
          title: $fn.value.pgettext('Contract', 'Approve'),
          date: $fn.value.pgettext('Contract', 'Approval date'),
          dname: 'approval_date',
          displayDate: true,
        };

      case 'signed':
        return {
          title: $fn.value.pgettext('Contract', 'Sign'),
          date: $fn.value.pgettext('Contract', 'Signature date'),
          dname: 'signature_date',
          displayDate: true,
        };

      case 'rescinded':
        return {
          title: $fn.value.pgettext('Contract', 'Rescind'),
          date: $fn.value.pgettext('Contract', 'Rescission date'),
          dname: 'rescission_date',
          displayDate: true,
        };

      case 'active':
        return {
          title: $fn.value.pgettext('Contract', 'Activate'),
          date: $fn.value.pgettext('Contract', 'Valid from'),
          dname: 'valid_from',
          displayDate: true,
        };

      case 'terminated':
        return {
          title: $fn.value.pgettext('Contract', 'Terminate'),
          date: $fn.value.pgettext('Contract', 'Termination date'),
          effective_date: $fn.value.pgettext(
            'Contract',
            'Termination effective date'
          ),
          dname: 'termination_date',
          displayDate: true,
        };

      case 'ended':
        return {
          title: $fn.value.pgettext('Contract', 'End'),
          date: $fn.value.pgettext('Contract', 'Ending date'),
          dname: 'end_date',
          displayDate: true,
        };

      case 'archived':
        return {
          title: $fn.value.pgettext('Contract', 'Archive'),
          date: $fn.value.pgettext('Contract', 'Archive date'),
          dname: 'archive_date',
          displayDate: true,
        };

      case 'unarchive':
        return {
          title: $fn.value.pgettext('Contract', 'Unarchive'),
          displayDate: false,
          notesRequired: true,
          notesHelpText: $fn.value.pgettext(
            'Contract',
            'The reason for unarchiving must be justified.'
          ),
        };
    }
  };
  const defaultData = (...args) => {
    const [status, contract] = args;

    switch (status) {
      case 'active': {
        return { date: contract.lifecycle.valid_from || $fn.value.today() };
      }

      case 'unarchive': {
        return { notes: null };
      }

      case 'terminated':
      case 'ended':
      case 'archived': {
        return { date: contract.lifecycle.valid_to || $fn.value.today() };
      }
    }
  };

  const options = ref(null);
  const formId = ref(null);
  const labelAlign = ref('right');
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    options: options.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_options = () => {
    return getCurrentOptions(props.status);
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return _.extend(
      {
        id: props.contract.id,
        rev: props.contract.revision_number,
        status: props.status,
        date: $fn.value.today(),
        effective_date: null,
        notes: null,
        $etag: props.contract.etag,
      },
      defaultData(props.status, props.contract)
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'options' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      options: $$load_options,
      formId: $$load_formId,
      item: $$load_item,
    },
    ux: {},
    dataRefs: {
      options,
      formId,
      labelAlign,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return { title: [props.contract.contract_number, options.value.title] };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.status,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('status');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_1qrsierj4_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_5wy47jbnm_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_nyd1ipusu_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_contract_status',
      data: _.set(item.value, options.value.dname, item.value.date),
    };
  };

  const act_7315092382398562_closeModal_1qrsierj4 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_1qrsierj4_getActionArgs,
    });
  const inst_evh_7315092382398562_close_hvttwown9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_1qrsierj4,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_hvttwown9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_jisc7oqac =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_76l5fzv97 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_jisc7oqac,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_76l5fzv97],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_bfqqvhic5 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_st4qp2he8 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_bfqqvhic5,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_st4qp2he8],
    event: 'close',
  });
  const act_1040167445267876_closeModal_5wy47jbnm =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_5wy47jbnm_getActionArgs,
    });
  const inst_evh_1040167445267876_success_6t8x6uxvl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_5wy47jbnm,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_nyd1ipusu = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_nyd1ipusu_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_6t8x6uxvl],
  });
  const inst_evh_1040167445267876_submit_3y0mai4dm =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_nyd1ipusu,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_3y0mai4dm],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_vc0nsbu8c =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_bka3cfkol =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_vc0nsbu8c,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_bka3cfkol],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_hvttwown9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_76l5fzv97,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_st4qp2he8,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_6t8x6uxvl,
    inst_evh_1040167445267876_submit_3y0mai4dm,
    submit: inst_evh_1040167445267876_submit_3y0mai4dm,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_bka3cfkol,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_hvttwown9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_hvttwown9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_76l5fzv97 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_76l5fzv97.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_st4qp2he8 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_st4qp2he8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_6t8x6uxvl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_6t8x6uxvl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_3y0mai4dm = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_3y0mai4dm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_bka3cfkol = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_bka3cfkol.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractStatusTransition"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        autofocus="first"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="date"
            :label="options.date"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            required
            v-if="options.displayDate"
          >
            <ItpFormDatetime name="date" v-model="item.date" required>
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="effective_date"
            :label="options.effective_date"
            :label-cols-md="4"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            required
            v-if="options.effective_date"
          >
            <ItpFormDatetime
              name="effective_date"
              v-model="item.effective_date"
              required
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="notes"
            :label="pgettext('ContractStatusTransition', 'Notes')"
            :label-cols-md="4"
            :content-cols-md="8"
            :labelAlign="labelAlign"
            :required="options.notesRequired"
            :description="options.notesHelpText"
          >
            <ItpFormTextarea
              name="notes"
              v-model="item.notes"
              :required="options.notesRequired"
            >
            </ItpFormTextarea>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
